import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import './PopulationSlider.css';

export const PopulationSlider = ({
    id,
    title,
    titleColor = '#f27929',
    minValue = 0,
    maxValue = 1400000,
    initialMin = 0,
    initialMax = 1400000,
    value = null, // Current value from parent (with minThreshold and maxThreshold)
    onChange,
    step = 1000,
    colorPalette = null, // New prop for custom colors
    resetTrigger = 0, // New prop to trigger reset
    onClear = null // New prop for clear functionality
}) => {
    // Initialize state from value prop if available, otherwise use initialMin/initialMax
    const [minThreshold, setMinThreshold] = useState(
        value?.minThreshold ?? initialMin
    );
    const [maxThreshold, setMaxThreshold] = useState(
        value?.maxThreshold ?? initialMax
    );

    // Update state when value prop changes (e.g., when reopening the dialog)
    // Only update if values actually changed to prevent infinite loops
    useEffect(() => {
        if (value?.minThreshold !== undefined && 
            value?.maxThreshold !== undefined &&
            (value.minThreshold !== minThreshold || value.maxThreshold !== maxThreshold)) {
            setMinThreshold(value.minThreshold);
            setMaxThreshold(value.maxThreshold);
        }
    }, [value, minThreshold, maxThreshold]);

    // Reset slider when resetTrigger changes
    useEffect(() => {
        if (resetTrigger > 0) {
            setMinThreshold(initialMin);
            setMaxThreshold(initialMax);
            // Don't call notifyChange here to avoid unnecessary updates during reset
        }
    }, [resetTrigger, initialMin, initialMax]);

    // Handle reset button click
    const handleReset = () => {
        setMinThreshold(initialMin);
        setMaxThreshold(initialMax);
        notifyChange(initialMin, initialMax);
    };

    // Handle clear button click
    const handleClear = () => {
        if (onClear) {
            onClear(id);
            // Also reset the ranges
            setMinThreshold(initialMin);
            setMaxThreshold(initialMax);
        }
    };

    // Default yellow color palette for population thresholds
    const defaultColorPalette = [
        '#FFF59D', // Light yellow
        '#FFEB3B', // Yellow
        '#FDD835', // Medium yellow
        '#F9A825', // Darker yellow
        '#FF8F00', // Orange-yellow
        '#FF6F00', // Dark orange-yellow
        '#E65100', // Deep orange
        '#BF360C'  // Very dark orange-red
    ];

    // Predefined population thresholds with configurable colors
    const getPopulationThresholds = () => {
        const colors = colorPalette || defaultColorPalette;
        return [
            { min: 0, max: 50000, label: 'Less than 50,000', color: colors[0] || '#FFF59D' },
            { min: 50000, max: 100000, label: '50,000-100,000', color: colors[1] || '#FFEB3B' },
            { min: 100000, max: 250000, label: '100,000-250,000', color: colors[2] || '#FDD835' },
            { min: 250000, max: 500000, label: '250,000-500,000', color: colors[3] || '#F9A825' },
            { min: 500000, max: 750000, label: '500,000-750,000', color: colors[4] || '#FF8F00' },
            { min: 750000, max: 1000000, label: '750,000-1M', color: colors[5] || '#FF6F00' },
            { min: 1000000, max: 1200000, label: '1M-1.2M', color: colors[6] || '#E65100' },
            { min: 1200000, max: 1400000, label: '1.2M-1.4M', color: colors[7] || '#BF360C' }
        ];
    };

    const populationThresholds = getPopulationThresholds();

    // Get active population ranges based on current slider values
    const getActiveRanges = () => {
        return populationThresholds.filter(threshold => {
            // Include range if it overlaps with current min/max thresholds
            return !(threshold.max <= minThreshold || threshold.min >= maxThreshold);
        });
    };

    const handleMinChange = (e) => {
        let newMin = parseInt(e.target.value);
        
        // Ensure we can always get back to the exact minimum value
        // If we're within one step of the min value, snap to min
        if (newMin < minValue + step && newMin > minValue) {
            newMin = minValue;
        }
        
        if (newMin <= maxThreshold) {
            setMinThreshold(newMin);
            notifyChange(newMin, maxThreshold);
        }
    };

    const handleMaxChange = (e) => {
        let newMax = parseInt(e.target.value);
        
        // Ensure we can always get back to the exact maximum value
        // If we're within one step of the max value, snap to max
        if (newMax > maxValue - step && newMax < maxValue) {
            newMax = maxValue;
        }
        
        if (newMax >= minThreshold) {
            setMaxThreshold(newMax);
            notifyChange(minThreshold, newMax);
        }
    };

    const notifyChange = (min, max) => {
        const activeRanges = populationThresholds.filter(threshold => {
            return !(threshold.max <= min || threshold.min >= max);
        });
        
        if (onChange) {
            onChange(id, {
                minThreshold: min,
                maxThreshold: max,
                activeRanges: activeRanges,
                colors: activeRanges.map(range => range.color), // Include colors in the data
                communitySize: activeRanges.map(range => {
                    if (range.max <= 1000) return 'less_1000';
                    if (range.min >= 1000 && range.max <= 5000) return '1000_5000';
                    if (range.min >= 5000 && range.max <= 15000) return '5000_15000';
                    if (range.min >= 15000 && range.max <= 30000) return '15000_30000';
                    if (range.min >= 30000 && range.max <= 50000) return '30000_50000';
                    if (range.min >= 50000) return '50000_plus';
                    return null;
                }).filter(Boolean)
            });
        }
    };

    const formatValue = (value) => {
        if (value >= 1000000) {
            return `${(value / 1000000).toFixed(1)}M`;
        } else if (value >= 1000) {
            return `${(value / 1000).toFixed(0)}K`;
        }
        return value.toString();
    };

    // Helper function to determine if text should be dark on light backgrounds
    const shouldUseDarkText = (color) => {
        // Light yellow colors that need dark text
        const lightColors = ['#FFF59D', '#FFEB3B', '#FDD835', '#F9A825'];
        return lightColors.includes(color);
    };

    const activeRanges = getActiveRanges();

    return (
        <div className="population-slider-container">
            <div className="filter-inner-container">
                <h4 className="filter-label" style={{ color: titleColor }}>{title}</h4>
                
                {/* Dual Range Slider */}
                <div className="dual-range-slider">
                    <div className="slider-track">
                        <div 
                            className="slider-range" 
                            style={{
                                left: `${((minThreshold - minValue) / (maxValue - minValue)) * 100}%`,
                                width: `${((maxThreshold - minThreshold) / (maxValue - minValue)) * 100}%`
                            }}
                        />
                    </div>
                    
                    <input
                        type="range"
                        min={minValue}
                        max={maxValue}
                        step={step}
                        value={minThreshold}
                        onChange={handleMinChange}
                        className="slider-input slider-min"
                    />
                    
                    <input
                        type="range"
                        min={minValue}
                        max={maxValue}
                        step={step}
                        value={maxThreshold}
                        onChange={handleMaxChange}
                        className="slider-input slider-max"
                    />
                </div>

                {/* Value Display */}
                <div className="slider-values">
                    <div className="value-display">
                        <span className="value-label">Min:</span>
                        <span className="value-number">{formatValue(minThreshold)}</span>
                    </div>
                    <div className="value-display">
                        <span className="value-label">Max:</span>
                        <span className="value-number">{formatValue(maxThreshold)}</span>
                    </div>
                </div>

                {/* Buttons Container */}
                <div className="buttons-container">
                    <button 
                        className="reset-button"
                        onClick={handleReset}
                        type="button"
                    >
                        Reset Range
                    </button>
                    {onClear && (
                        <button 
                            className="clear-button"
                            onClick={handleClear}
                            type="button"
                        >
                            Clear
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

PopulationSlider.propTypes = {
    id: PropTypes.string.isRequired,
    title: PropTypes.string,
    titleColor: PropTypes.string,
    minValue: PropTypes.number,
    maxValue: PropTypes.number,
    initialMin: PropTypes.number,
    initialMax: PropTypes.number,
    value: PropTypes.shape({
        minThreshold: PropTypes.number,
        maxThreshold: PropTypes.number
    }),
    onChange: PropTypes.func,
    step: PropTypes.number,
    colorPalette: PropTypes.arrayOf(PropTypes.string), // New prop for custom colors
    resetTrigger: PropTypes.number, // New prop to trigger reset
    onClear: PropTypes.func // New prop for clear functionality
}; 